import React, { Children, forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import Tooltip, { TooltipLabelId, } from '@polestar/component-warehouse-react/components/tooltip/Tooltip';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import styled from '@emotion/styled';
import AttachedPopover from '@polestar/component-warehouse-react/components/attached-popover/AttachedPopover';
import Positions from '@polestar/component-warehouse-react/tokens/positions/Positions';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { minimalHitbox } from '@polestar/component-warehouse-react/helpers/style';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
import { css } from '@emotion/react';
const WithTooltip = forwardRef(({ children, placement, forcePlacement = false, minimumWidth = 240, minimalHitboxType, preferredPlacement, ...props }, ref) => {
    // throw error when multiple children are provided
    const child = Children.only(children);
    const memoizedId = useSSRSafeId('tooltip', props.id);
    const [visible, setVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [calculatedPlacement, setCalculatedPlacement] = useState();
    const [popoverRef, popoverRefNode] = useCallbackRef();
    const { isRTL } = useDirection();
    const [internalRef, tooltipAttachElement] = useCallbackRef();
    const combinedRef = useCombinedRef(ref, internalRef);
    const tooltipOffset = useRef({ x: 0 });
    useEffect(() => {
        const hideTooltip = () => {
            setVisible(false);
        };
        window.addEventListener('resize', hideTooltip);
        window.addEventListener('wheel', hideTooltip);
        return () => {
            window.removeEventListener('resize', hideTooltip);
            window.removeEventListener('wheel', hideTooltip);
        };
    }, []);
    /**
     * Tell scaffold to show the tooltip
     */
    const handleFocus = useCallback(() => {
        var _a, _b;
        setVisible(true);
        setIsFocused(true);
        (_b = (_a = child.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [child.props]);
    /**
     * MouseEnter handler
     */
    const handleMouseEnter = useCallback(() => {
        var _a, _b;
        setVisible(true);
        (_b = (_a = child.props).onMouseEnter) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [child.props]);
    /**
     * Touch start handler
     */
    const handleTouchStart = useCallback((e) => {
        var _a, _b;
        e.stopPropagation();
        setVisible(true);
        (_b = (_a = child.props).onTouchStart) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [child.props]);
    /**
     * MouseLeave handler
     */
    const handleMouseLeave = useCallback((e) => {
        var _a, _b, _c;
        // when the mouse is coming from above and the AttachedPopover is placed `top center`
        // it will force a mouseLeave event that needs to be ignored to keep the tooltip
        // visible.
        //
        // ?.outerHTML is needed because @testing-library mocks it but not as a DOM Node and
        // will break the tests while it works inside the browser.
        if (!!((_a = e.relatedTarget) === null || _a === void 0 ? void 0 : _a.outerHTML) &&
            (popoverRefNode === null || popoverRefNode === void 0 ? void 0 : popoverRefNode.contains(e.relatedTarget))) {
            // ignore
        }
        else {
            setVisible(false);
            (_c = (_b = child.props).onMouseLeave) === null || _c === void 0 ? void 0 : _c.call(_b);
        }
    }, [child.props, popoverRefNode]);
    const handleBlur = useCallback(() => {
        var _a, _b;
        setVisible(false);
        setIsFocused(false);
        (_b = (_a = child.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a);
    }, [child.props]);
    const handlePlacementChange = useCallback((placement, offset) => {
        tooltipOffset.current = offset;
        setCalculatedPlacement(placement);
    }, [tooltipOffset]);
    const handleClose = useCallback(() => {
        if (isFocused)
            return; // don't close when container is still focused
        setVisible(false);
    }, [isFocused]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            setVisible(false);
        }
    }, []);
    // only force placement when provided
    const placementProps = preferredPlacement
        ? { preferredPlacement }
        : placement
            ? { preferredPlacement: placement }
            : {
                preferredPlacement: (isRTL
                    ? [
                        Positions.topCenter,
                        Positions.rightCenter,
                        Positions.leftCenter,
                        Positions.topRight,
                        Positions.topLeft,
                        Positions.bottomCenter,
                        Positions.bottomRight,
                        Positions.bottomLeft,
                    ]
                    : [
                        Positions.topCenter,
                        Positions.leftCenter,
                        Positions.rightCenter,
                        Positions.topLeft,
                        Positions.topRight,
                        Positions.bottomCenter,
                        Positions.bottomLeft,
                        Positions.bottomRight,
                    ]),
            };
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { as: child.type, key: child.key, ...child.props, ref: combinedRef, tabIndex: 0, minimalHitboxType: minimalHitboxType, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onTouchStart: handleTouchStart, onFocus: handleFocus, onKeyDown: handleKeyDown, onBlur: handleBlur, ...aria({
                describedby: visible && `${memoizedId}-content-${TooltipLabelId}`,
                role: 'button',
            }), "data-type": "tooltip" }),
        visible && (React.createElement(AttachedPopover, { id: memoizedId, attachTo: tooltipAttachElement, backdropProps: { visible: false }, onPlacementChange: handlePlacementChange, onClose: handleClose, offset: {
                top: Spacings.xSmall,
                bottom: Spacings.xSmall,
                left: Spacings.xSmall,
                right: Spacings.xSmall,
            }, minimumDimensions: { width: minimumWidth }, ...placementProps, forcePlacement: forcePlacement, stretch: false, isHiddenWhenScrolled: !isFocused, onMouseLeave: handleMouseLeave, ref: popoverRef },
            React.createElement(Tooltip, { ...props, id: `${memoizedId}-content`, placement: calculatedPlacement, arrowOffset: { x: tooltipOffset.current.x } }, child)))));
});
WithTooltip.displayName = 'WithTooltip';
export default WithTooltip;
// TODO: double check this #a11y
const Container = styled.div(({ minimalHitboxType }) => css(minimalHitboxType === MinimalHitboxTypes.both && minimalHitbox));
